import {
  Table,
  Button,
  Divider,
  Select,
  Input,
  message,
  Modal,
  Form,
  Spin,
  Popconfirm,
} from 'antd'
import React, { useEffect, useState } from 'react'
import moment from 'moment'
import {
  getAllGameTags,
  getGameTagsCategory,
  editGameTag,
  getAllGamesByTag,
  deleteGameByTag,
  addGameByTag,
  addGameTag,
  addGamesByTag,
} from 'src/api/tag'
import { getGamesByCategory } from 'src/api/game'
import { getSurvivalZone } from 'src/api/survivalzone'
import { getMazes } from 'src/api/maze'
import { getTreasureHunter } from 'src/api/treasurehunter'
import {
  EditOutlined,
  PlusOutlined,
  AppstoreAddOutlined,
} from '@ant-design/icons'
const { Column } = Table
const { Option } = Select
const { Search } = Input
const { Item } = Form
import './GamesTags.css'

interface GameTag {
  id: string
  nameEn: string
  nameAr: string
  nameFr: string
  category: string
  modifiedOn: number
}

interface ApiResponse {
  data: GameTag[]
  current_page: number
  number_of_elements: number
  total_pages: number
  total_items: number
}

interface EditFormData {
  nameEn: string
  nameAr: string
  nameFr: string
  category: string
}

interface Game {
  id: string
  title: string
}

const GameTagPage: React.FC = () => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [gameTags, setGameTags] = useState<GameTag[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })
  const [filters, setFilters] = useState({
    name: '',
    category: '',
  })
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [gamesModalVisible, setGamesModalVisible] = useState(false)
  const [editingTag, setEditingTag] = useState<GameTag | null>(null)
  const [editLoading, setEditLoading] = useState(false)
  const [tagGames, setTagGames] = useState<Game[]>([])
  const [gamesLoading, setGamesLoading] = useState(false)
  const [allGames, setAllGames] = useState<any[]>([])
  const [allGuys, setAllGuys] = useState<any[]>([])
  const [allMazes, setAllMazes] = useState<any[]>([])
  const [allTreasureHunter, setAllTreasureHunter] = useState<any[]>([])
  const [selectedGamesToAdd, setSelectedGamesToAdd] = useState<string[]>([])

  const [addingLoading, setAddingLoading] = useState(false)
  const [isCreateMode, setIsCreateMode] = useState(false)

  const formatCategoryLabel = (category: string): string => {
    const formatMap: Record<string, string> = {
      GENRE: 'Genre',
      DESCRIPTIVE: 'Descriptive',
      MOOD_EMOTION: 'Mood & Emotion',
      ACTIVITY: 'Activity',
      CULTURAL_POPULAR_SIMILAR_BRANDS: 'Cultural/Popular/Similar Brands',
    }

    return (
      formatMap[category] ||
      category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    )
  }

  const fetchGames = async () => {
    const response = await getGamesByCategory(0, 300)
    setAllGames(response.data || [])
  }

  const fetchGuys = async () => {
    const response = await getSurvivalZone(0, 300)
    setAllGuys(response.data || [])
  }

  const fetchTreasureHunter = async () => {
    const response = await getTreasureHunter(0, 300)
    setAllTreasureHunter(response.data || [])
  }

  const fetchMazes = async () => {
    const response = await getMazes(0, 300)
    setAllMazes(response.data || [])
  }

  const fetchGameTags = async (page = 0, size = 10) => {
    try {
      setLoading(true)
      const response: ApiResponse = await getAllGameTags(
        page,
        size,
        filters.name || undefined,
        filters.category || undefined
      )

      setGameTags(response.data)
      setPagination({
        current: response.current_page + 1,
        pageSize: size,
        total: response.total_items,
      })
    } catch (error) {
      message.error('Failed to fetch game tags')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const categoriesData = await getGameTagsCategory()
      setCategories(categoriesData)
    } catch (error) {
      message.error('Failed to fetch categories')
      console.error(error)
    }
  }

  const fetchGamesByTag = async (tagId: string, page = 0, size = 50) => {
    try {
      setGamesLoading(true)
      const response = await getAllGamesByTag(page, size, tagId)
      setTagGames(response.data || [])
    } catch (e) {
      message.error('Failed to load games for tag')
    } finally {
      setGamesLoading(false)
    }
  }

  const handleTableChange = (newPagination: any) => {
    fetchGameTags(newPagination.current - 1, newPagination.pageSize)
  }

  const handleSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, name: value }))
  }

  const handleCategoryChange = (value: string) => {
    setFilters((prev) => ({ ...prev, category: value }))
  }

  const handleEditClick = async (tag: GameTag) => {
    setIsCreateMode(false)
    setEditingTag(tag)

    form.setFieldsValue({
      nameEn: tag.nameEn,
      nameAr: tag.nameAr,
      nameFr: tag.nameFr,
      category: tag.category,
    })

    setEditModalVisible(true)
  }

  const handleManageGamesClick = async (tag: GameTag) => {
    setEditingTag(tag)
    setSelectedGamesToAdd([])
    await fetchGamesByTag(tag.id)
    setGamesModalVisible(true)
  }

  const combinedOptions = [
    ...allGames.map((g) => ({
      label: `🎮 ${g.title}`,
      value: g.id,
    })),
    ...allMazes.map((m) => ({ label: `🌀 ${m.title}`, value: m.id })),
    ...allTreasureHunter.map((t) => ({ label: `💰 ${t.title}`, value: t.id })),
    ...allGuys.map((g) => ({ label: `🏃 ${g.title}`, value: g.id })),
  ]

  const filteredOptions = React.useMemo(() => {
    const linkedGameIds = new Set(tagGames.map((game) => game.id))
    return combinedOptions.filter((option) => !linkedGameIds.has(option.value))
  }, [combinedOptions, tagGames])

  const handleAddGameToTag = async () => {
    if (!editingTag || selectedGamesToAdd.length === 0) return

    try {
      setAddingLoading(true)
      await addGamesByTag(selectedGamesToAdd, editingTag.id)
      message.success('Games added successfully')
      setSelectedGamesToAdd([])
      await fetchGamesByTag(editingTag.id)
    } catch (e) {
      message.error('Failed to add games')
    } finally {
      setAddingLoading(false)
    }
  }

  const normalizeTagName = (value?: string) =>
    value ? value.replace(/^#+/, '').trim() : value

  useEffect(() => {
    fetchGameTags()
    fetchCategories()
  }, [filters])

  useEffect(() => {
    fetchGames()
    fetchMazes()
    fetchTreasureHunter()
    fetchGuys()
  }, [])

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', gap: 16 }}>
          <Input
            placeholder="Search by name"
            allowClear
            style={{ width: 200 }}
            value={filters.name}
            onChange={(e) => handleSearch(e.target.value)}
          />

          <Select
            onChange={handleCategoryChange}
            allowClear
            placeholder="Filter by category"
            style={{ width: 250 }}
            value={filters.category || undefined}
          >
            {categories.map((category) => (
              <Option value={category} key={category}>
                {formatCategoryLabel(category)}
              </Option>
            ))}
          </Select>
        </div>

        <Button
          type="primary"
          onClick={() => {
            setIsCreateMode(true)
            setEditingTag(null)
            form.resetFields()
            setEditModalVisible(true)
          }}
          className="btn-primary"
          style={{ width: 140 }}
        >
          <PlusOutlined /> Create
        </Button>
      </div>

      <div style={{ marginTop: 10 }}>
        <Table
          rowKey="id"
          dataSource={gameTags}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          className=""
        >
          <Column
            dataIndex="nameEn"
            title="Name (English)"
            render={(data) => data}
          />
          <Column dataIndex="nameAr" title="Name (Arabic)" />
          <Column dataIndex="nameFr" title="Name (French)" />
          <Column
            dataIndex="category"
            title="Category"
            render={(category: string) => formatCategoryLabel(category)}
          />
          <Column
            dataIndex="modifiedOn"
            title="Modified Date"
            render={(data) =>
              data ? moment(data).format('YYYY-MM-DD HH:mm:ss') : '-'
            }
          />
          <Column
            title=""
            width={150}
            render={(_, record: GameTag) => (
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  icon={<EditOutlined style={{ color: '#f3109e' }} />}
                  type="link"
                  onClick={() => handleEditClick(record)}
                  className=""
                  style={{ width: 140 }}
                >
                  Edit{' '}
                </Button>
              </div>
            )}
          />

          <Column
            title=""
            width={150}
            render={(_, record: GameTag) => (
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  icon={<AppstoreAddOutlined style={{ color: '#52c41a' }} />}
                  type="link"
                  onClick={() => handleManageGamesClick(record)}
                >
                  Manage Games
                </Button>
              </div>
            )}
          />
        </Table>
      </div>
      <Modal
        title={isCreateMode ? 'Create Game Tag' : 'Edit Game Tag'}
        visible={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false)
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            try {
              setEditLoading(true)

              const payload = {
                nameEn: normalizeTagName(values.nameEn),
                nameAr: normalizeTagName(values.nameAr),
                nameFr: normalizeTagName(values.nameFr),
                category: values.category,
              }

              if (isCreateMode) {
                await addGameTag(
                  payload.nameEn,
                  payload.category,
                  payload.nameAr,
                  payload.nameFr
                )
                message.success('Tag created successfully')
              } else {
                await editGameTag(
                  editingTag!.id,
                  payload.nameEn,
                  payload.nameAr,
                  payload.nameFr,
                  payload.category
                )
                message.success('Tag updated successfully')
              }
              setEditModalVisible(false)
              form.resetFields()
              fetchGameTags(pagination.current - 1, pagination.pageSize)
            } catch (e) {
              message.error(
                isCreateMode ? 'Tag Already Exist' : 'Tag Already Exist'
              )
              setEditModalVisible(false)
            } finally {
              setEditLoading(false)
            }
          }}
        >
          <Item label="English Name" name="nameEn" rules={[{ required: true }]}>
            <Input placeholder="Enter English name" />
          </Item>

          <Item label="Arabic Name" name="nameAr">
            <Input placeholder="Enter Arabic name" />
          </Item>

          <Item label="French Name" name="nameFr">
            <Input placeholder="Enter French name" />
          </Item>

          <Item label="Category" name="category" rules={[{ required: true }]}>
            <Select placeholder="Select category">
              {categories.map((category) => (
                <Option value={category} key={category}>
                  {formatCategoryLabel(category)}
                </Option>
              ))}
            </Select>
          </Item>

          <Item style={{ marginTop: 24, marginBottom: 0 }}>
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}
            >
              <Button onClick={() => setEditModalVisible(false)}>Cancel</Button>
              <Button
                type="primary"
                className="btn-primary"
                style={{ width: 140 }}
                htmlType="submit"
                loading={editLoading}
              >
                Save Changes
              </Button>
            </div>
          </Item>
        </Form>
      </Modal>
      <Modal
        title={`Manage Games - ${editingTag?.nameEn || ''}`}
        visible={gamesModalVisible}
        onCancel={() => {
          setGamesModalVisible(false)
          setSelectedGamesToAdd([])
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          <Select
            mode="multiple"
            showSearch
            allowClear
            placeholder="Select Games, Mazes, etc."
            style={{ flex: 1 }}
            optionFilterProp="label"
            value={selectedGamesToAdd}
            onChange={setSelectedGamesToAdd}
            options={filteredOptions}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddGameToTag}
            loading={addingLoading}
            disabled={selectedGamesToAdd.length === 0}
            className="btn-primary"
            style={{ width: 100 }}
          >
            Add
          </Button>
        </div>

        {gamesLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
          </div>
        ) : (
          <div className="linked-games-container">
            {tagGames.length === 0 ? (
              <div className="empty-linked-games">
                No games linked to this tag
              </div>
            ) : (
              tagGames.map((game) => (
                <Popconfirm
                  key={game.id}
                  title="Remove this item from tag?"
                  onConfirm={async () => {
                    try {
                      await deleteGameByTag(editingTag!.id, game.id)
                      message.success('Item removed successfully')
                      await fetchGamesByTag(editingTag!.id)
                    } catch (e) {
                      message.error('Failed to remove game')
                    }
                  }}
                >
                  <div className="game-pill">
                    <span className="game-name">{game.title}</span>
                    <span className="game-remove">✕</span>
                  </div>
                </Popconfirm>
              ))
            )}
          </div>
        )}
      </Modal>
    </>
  )
}

GameTagPage.displayName = 'GameTagPage'
export default GameTagPage
